<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import { visibleAreas } from '@/navigation/areas';
import { AREA_ICONS } from '@/navigation/navIcons';
import { areaIdForRoute } from '@/navigation/activeArea';
import { areaEntry } from '@/navigation/areaSpots';
import { useAuthStore } from '@/stores/authStore';
import { isGateOpen } from '@/navigation/gates';
import { useCommandPaletteStore } from '@/components/commandPalette/commandPaletteStore';

/**
 * On a phone, the areas sit in a floating bar along the bottom, with a round
 * Search button beside it, as Macro's phone app does. The bar rides above the
 * docked audio player, using the height the player publishes.
 *
 * The bar floats over the page. Each page's scroll area leaves room under its
 * last item with `max-md:pb-mobile-area-bar-clearance`, defined in app.css. If
 * the bar's height or its 12px offset changes, change that token too.
 */

const route = useRoute();
const authStore = useAuthStore();
const palette = useCommandPaletteStore();


const areas = computed(() => visibleAreas(isGateOpen).filter((area) => area.id !== 'settings'));
const activeAreaId = computed(() => areaIdForRoute(route));
</script>

<template>
  <div
    class="pointer-events-none fixed inset-x-0 z-30 flex items-center justify-center gap-2 px-3 md:hidden"
    style="bottom: calc(var(--global-audio-player-height, 0px) + 12px + env(safe-area-inset-bottom, 0px))"
    data-testid="mobile-area-bar"
  >
    <!-- `min-w-0` on the bar and its buttons lets them narrow when Safari's
         text zoom makes the screen narrower than the bar. Without it the bar
         ran off the right edge and cut the Search button off. -->
    <nav aria-label="Areas" class="pointer-events-auto flex h-14 min-w-0 items-center gap-1 rounded-full bg-background/95 px-2 shadow-lg ring-1 ring-border backdrop-blur">
      <router-link
        v-for="area in areas"
        :key="area.id"
        :to="areaEntry(area, authStore.userData?.id)"
        :aria-label="area.label"
        :aria-current="activeAreaId === area.id ? 'page' : undefined"
        class="flex size-11 min-w-0 items-center justify-center rounded-full text-muted-foreground aria-[current=page]:bg-muted aria-[current=page]:text-foreground"
      >
        <component :is="AREA_ICONS[area.id]" class="size-6" aria-hidden="true" />
      </router-link>
    </nav>
    <button
      type="button"
      aria-label="Search everything"
      data-testid="mobile-search"
      class="pointer-events-auto flex size-14 shrink-0 items-center justify-center rounded-full bg-background/95 text-foreground shadow-lg ring-1 ring-border backdrop-blur"
      @click="palette.open()"
    >
      <MagnifyingGlassIcon class="size-6" aria-hidden="true" />
    </button>
  </div>
</template>
